/**
 * Disposal Recon API
 * Fetches disposal tools, updates tool fields, computes stats.
 * All disposal recon writes route through update_tool (Tools container).
 */

import { apiFetch } from '../../../auth/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

/* ── Internal: snake_case → camelCase mapper ─────────────────────────────── */

function transformDisposalTool(raw) {
  return {
    id:                     raw.id,
    siteId:                 raw.site_id,
    toolId:                 raw.supplier_tool_id,
    altToolId:              raw.alt_tool_id,
    oem:                    raw.oem,
    listNumber:             raw.list_number,
    reconStatus:            raw.recon_status,
    workflowStatus:         raw.workflow_status,
    toolType:               raw.tool_type,
    platform:               raw.platform,
    partDescription:        raw.part_description,
    toolDescription:        raw.tool_description,
    cspc:                   raw.cspc,
    barcode:                raw.barcode,
    qrCode:                 raw.qr_code,
    quantity:               raw.quantity,
    dieOp:                  raw.die_op,
    toolCode:               raw.tool_code,
    cavities:               raw.cavities,
    found:                  raw.found,
    addedByTmg:             raw.added_by_tmg,
    missingFromLastImport:  raw.missing_from_last_import,
    tierLevel:              raw.tier_level,
    tierDuns:               raw.tier_duns,
    shipToLocation:         raw.ship_to_location,
    lastShipDate:           raw.last_ship_date,
    capacityFlag:           raw.capacity_flag,
    npnFlag:                raw.npn_flag,
    siteLocation:           raw.site_location,
    detailLocation:         raw.detail_location,
    rowNbr:                 raw.row_nbr,
    stackNbr:               raw.stack_nbr,
    rackNbr:                raw.rack_nbr,
    shelfNbr:               raw.shelf_nbr,
    skidNbr:                raw.skid_nbr,
    inventoryComments:      raw.inventory_comments,
    tmgDataComments:        raw.tmg_data_comments,
    supplierStatus:         raw.supplier_status,
    supplierComments:       raw.supplier_comments,
    reconComments:          raw.recon_comments,
    preliminaryToolStatus:  raw.preliminary_tool_status,
    approvedScrap:          raw.approved_scrap,
    supplierConfirmedDate:  raw.supplier_confirmed_date,
    supplierConfirmedBy:    raw.supplier_confirmed_by,
    retainedReason:         raw.retained_reason,
    invOemPnStatus:         raw.inv_oem_pn_status,
    invAssetTagStatus:      raw.inv_asset_tag_status,
    length:                 raw.length,
    width:                  raw.width,
    height:                 raw.height,
    estimatedWeight:        raw.estimated_weight,
    weightUnit:             raw.weight_unit,
    lastEnrichmentDate:     raw.last_enrichment_date,
    createdAt:              raw.created_at,
    createdBy:              raw.created_by,
    updatedAt:              raw.updated_at,
    updatedBy:              raw.updated_by,
    // Pass-through join fields (already correct casing)
    part_numbers:           raw.part_numbers,
    asset_tags:             raw.asset_tags,
    pn_count:               raw.pn_count,
    completeness:           raw.completeness,
    site_name:              raw.site_name,
    supplier_name:          raw.supplier_name,
    _match_context:         raw._match_context || null,
  };
}

/* ── Internal: non-2xx → Error carrying status + parsed body ─────────────── */

/**
 * Throw for a non-2xx Response. Used ONLY by deleteDisposalTool,
 * restoreDisposalTool and getDeletedDisposalTools — not retrofitted onto the
 * older fetchers in this file.
 */
async function throwApiError(response, prefix) {
  // Keep status + body: the backend emits `reason` so the UI need not match on prose, and the sibling pattern above discards it.
  const body = await response.json().catch(() => ({}));
  const error = new Error(body.error || `${prefix} (${response.status})`);
  error.status = response.status;
  error.body = body;
  throw error;
}

/* ── Exports ─────────────────────────────────────────────────────────────── */

/**
 * Fetch all disposal tools for a site (with joins + completeness).
 * Returns array of transformed tool objects.
 */
export async function getDisposalTools(siteId, callerEmail, searchQuery = null) {
  const params = new URLSearchParams({
    action: 'list',
    site_id: siteId,
  });
  if (callerEmail) params.append('caller_email', callerEmail);
  if (searchQuery) params.append('search', searchQuery);

  const response = await apiFetch(`${API_BASE_URL}/get_disposal_tools?${params}`);
  if (!response.ok) throw new Error(`API error: ${response.status}`);

  const raw = await response.json();
  return (raw || []).map(transformDisposalTool);
}

/**
 * Canonical multi-container search (Tools + AssetTags + PartNumbers) for a
 * site, used when the query meets the minimum length. Routes through the
 * canonical search_inventory endpoint (NOT get_disposal_tools' legacy
 * _disposal_match_context). Raw snake_case results are mapped through
 * transformDisposalTool so the existing camelCase column rendering keeps
 * working; `_match_context` passes through the transform.
 * GET /search_inventory?site_id={siteId}&q={query}&caller_email={email}
 */
export async function searchDisposalReconTools(siteId, query, callerEmail) {
  const params = new URLSearchParams({ site_id: siteId, q: query });
  if (callerEmail) params.append('caller_email', callerEmail);

  const response = await apiFetch(`${API_BASE_URL}/search_inventory?${params}`);
  if (!response.ok) throw new Error(`Search failed: ${response.status}`);

  const raw = await response.json();
  return (raw || []).map(transformDisposalTool);
}

/**
 * Update a disposal tool via PATCH to update_tool.
 * Strips _match_context before sending. project_id may be null.
 */
export async function updateDisposalTool(toolId, fields, callerEmail, projectId = null) {
  const params = new URLSearchParams({
    tool_id: toolId,
  });
  if (callerEmail) params.append('caller_email', callerEmail);

  // Strip response-only annotation — must NEVER be written to Cosmos
  const { _match_context, ...cleanFields } = fields;
  const body = { ...cleanFields };
  if (projectId) body.project_id = projectId;

  const response = await apiFetch(`${API_BASE_URL}/update_tool?${params}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Save failed (${response.status})`);
  }

  return response.json();
}

/**
 * Soft-delete a tool via DELETE to delete_tool (ADMIN only).
 * toolId is the Cosmos document id (tool.id), NOT tool.toolId, which holds
 * supplier_tool_id — the handler point-reads read_item(item=tool_id, ...).
 * DELETE /delete_tool?tool_id={toolId}&site_id={siteId}
 */
export async function deleteDisposalTool(toolId, siteId) {
  // No caller_email: delete_tool resolves identity from the bearer via
  // resolve_principal and reads no caller_email param (audit E1).
  // Deliberate omission, not an oversight — do not add one to match
  // the sibling calls in this file.
  const params = new URLSearchParams({
    tool_id: toolId,
    site_id: siteId,
  });

  const response = await apiFetch(`${API_BASE_URL}/delete_tool?${params}`, {
    method: 'DELETE',
  });

  if (!response.ok) await throwApiError(response, 'Delete failed');

  return response.json();
}

/**
 * Restore a soft-deleted tool via POST to restore_tool (ADMIN only).
 * toolId is the Cosmos document id — from the recycle bin that is
 * row.tool_id; the handler point-reads read_item(item=tool_id, ...).
 * POST /restore_tool?tool_id={toolId}&site_id={siteId}
 */
export async function restoreDisposalTool(toolId, siteId) {
  // No caller_email: restore_tool resolves identity from the bearer via
  // resolve_principal and reads no caller_email param (audit E1).
  // Deliberate omission, not an oversight — do not add one to match
  // the sibling calls in this file.
  const params = new URLSearchParams({
    tool_id: toolId,
    site_id: siteId,
  });

  // POST with NO body and no Content-Type: the handler reads query params only.
  const response = await apiFetch(`${API_BASE_URL}/restore_tool?${params}`, {
    method: 'POST',
  });

  if (!response.ok) await throwApiError(response, 'Restore failed');

  return response.json();
}

/**
 * List soft-deleted tools at a site via get_deleted_tools (ADMIN only).
 * Returns the raw { tools: [...] } body, UNTRANSFORMED — the recycle bin
 * consumes the snake_case projection directly.
 * GET /get_deleted_tools?site_id={siteId}
 */
export async function getDeletedDisposalTools(siteId) {
  // No caller_email: get_deleted_tools resolves identity from the bearer via
  // resolve_principal and reads no caller_email param (audit E1).
  // Deliberate omission, not an oversight — do not add one to match
  // the sibling calls in this file.
  const params = new URLSearchParams({ site_id: siteId });

  const response = await apiFetch(`${API_BASE_URL}/get_deleted_tools?${params}`);

  if (!response.ok) await throwApiError(response, 'Could not load deleted tools');

  const body = await response.json();
  // Newest delete first. The get_deleted_tools query has NO ORDER BY, so this
  // is the only sort — rows otherwise arrive in whatever order Cosmos returns.
  // deleted_at can be ABSENT (a Cosmos projection omits absent keys): absent
  // sorts LAST, never first, and never throws. deleted_at is stamp_updated's
  // datetime.now(timezone.utc).isoformat(): always UTC, always "+00:00", so
  // string order is time order — including when isoformat drops the fraction at
  // microsecond 0 ("...:00+00:00" sorts before "...:00.123456+00:00").
  if (Array.isArray(body.tools)) {
    body.tools = [...body.tools].sort((a, b) => {
      const ta = typeof a.deleted_at === 'string' ? a.deleted_at : null;
      const tb = typeof b.deleted_at === 'string' ? b.deleted_at : null;
      if (ta === tb) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      return ta < tb ? 1 : -1;
    });
  }
  return body;
}

/**
 * Compute stats from an array of tools (pure function, no API call).
 */
export function getDisposalStats(tools) {
  const total = tools.length;
  const closed = tools.filter(t => t.reconStatus === 'CLOSED').length;
  const open = total - closed;
  const pctComplete = total > 0 ? Math.round((closed / total) * 100) : 0;
  return { total, open, closed, pctComplete };
}
